import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Avatar,
  Button,
  Flex,
  Input,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd'
import type { TableProps } from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import { getConversations } from '@/api/ai-chat/conversations'
import { getPrompt } from '@/api/ai-chat/prompt'
import type { ConversationListItem, PromptVersion } from '@/api/ai-chat/types'
import { OUTCOME_TAG_COLORS, fmtDevice, fmtDuration, fmtTime } from './utils'
import { ConversationModal } from './ConversationModal'

const { Title } = Typography

const OUTCOME_OPTIONS = [
  { value: '', label: 'All outcomes' },
  { value: 'appointment', label: 'Appointment' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'dnq', label: 'DNQ' },
  { value: 'not-interested', label: 'Not Interested' },
  { value: 'left', label: 'Left Chat' },
  { value: 'dnc', label: 'DNC' },
  { value: 'in-progress', label: 'In Progress' },
]

const STAGE_OPTIONS = [
  { value: '', label: 'All stages' },
  ...Array.from({ length: 6 }, (_, i) => ({
    value: String(i + 1),
    label: `Stage ${i + 1}`,
  })),
]

const CHANNEL_OPTIONS = [
  { value: '', label: 'All channels' },
  { value: 'web', label: 'Web' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
]

export const ConversationsPage: React.FC = () => {
  const [conversations, setConversations] = useState<ConversationListItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [outcome, setOutcome] = useState('')
  const [stage, setStage] = useState('')
  const [search, setSearch] = useState('')
  const [channel, setChannel] = useState('')
  const [promptName, setPromptName] = useState('')
  const [filterVersionNum, setFilterVersionNum] = useState('')
  const [allVersionObjects, setAllVersionObjects] = useState<PromptVersion[]>(
    [],
  )
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    getPrompt().then((data) => setAllVersionObjects(data.versions))
  }, [])

  const promptNameOptions = useMemo(() => {
    const names = [
      ...new Set(allVersionObjects.map((v) => v.name).filter(Boolean)),
    ].sort() as string[]
    return [
      { value: '', label: 'All prompts' },
      ...names.map((n) => ({ value: n, label: n })),
    ]
  }, [allVersionObjects])

  const isNamedPrompt = useCallback(
    (val: string) =>
      Boolean(val && allVersionObjects.some((v) => v.name === val)),
    [allVersionObjects],
  )

  const versionDropdownEnabled = !!(isNamedPrompt(promptName) && channel)

  const versionDropdownOptions = useMemo(() => {
    const base = [{ value: '', label: 'All versions' }]
    if (!versionDropdownEnabled) return base
    const nums = [
      ...new Set(
        allVersionObjects
          .filter((v) => v.name === promptName && (v.type || 'web') === channel)
          .map((v) => v.version_num)
          .filter((n): n is number => n != null),
      ),
    ].sort((a, b) => b - a)
    return [...base, ...nums.map((n) => ({ value: String(n), label: `v${n}` }))]
  }, [allVersionObjects, promptName, channel, versionDropdownEnabled])

  const activePromptVersions = useMemo((): string[] => {
    if (!promptName) return []
    if (allVersionObjects.some((v) => v.name === promptName)) {
      return allVersionObjects
        .filter((v) => {
          if (v.name !== promptName) return false
          if (filterVersionNum && String(v.version_num) !== filterVersionNum)
            return false
          return true
        })
        .map((v) => v.version)
        .filter((v): v is string => Boolean(v))
    }
    return [promptName]
  }, [promptName, filterVersionNum, allVersionObjects])

  const load = useCallback(
    async (p = page) => {
      setLoading(true)
      try {
        const data = await getConversations({
          page: p,
          ...(outcome && { outcome }),
          ...(stage && { stage }),
          ...(search.trim() && { search: search.trim() }),
          ...(channel && { channel }),
          ...(activePromptVersions.length && {
            prompt_versions: activePromptVersions,
          }),
        })
        setConversations(data.conversations)
        setTotal(data.total)
      } finally {
        setLoading(false)
      }
    },
    [page, outcome, stage, search, channel, activePromptVersions],
  )

  useEffect(() => {
    load(1)
    setPage(1)
    pollRef.current = setInterval(() => {
      if (!selectedId) load(page)
    }, 5000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [outcome, stage, channel, promptName, filterVersionNum]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setPage(1)
      load(1)
    }, 350)
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    }
  }, [search]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePromptNameChange = (val: string) => {
    setPromptName(val)
    setFilterVersionNum('')
  }

  const handleChannelChange = (val: string) => {
    setChannel(val)
    setFilterVersionNum('')
  }

  const handleDownloadCsv = () => {
    const base =
      (import.meta.env.VITE_AI_AGENT_API_URL || '') +
      '/api/dashboard/conversations/export'
    const qs = new URLSearchParams()
    if (outcome) qs.set('outcome', outcome)
    if (stage) qs.set('stage', stage)
    if (search.trim()) qs.set('search', search.trim())
    if (channel) qs.set('channel', channel)
    activePromptVersions.forEach((v) => qs.append('prompt_versions[]', v))
    window.location.href = `${base}?${qs}`
  }

  const columns: TableProps<ConversationListItem>['columns'] = [
    {
      title: 'Started',
      dataIndex: 'started_at',
      render: (v: string) => fmtTime(v),
    },
    {
      title: 'Company ID',
      dataIndex: 'company_id',
      render: (v: string | null) =>
        v ? (
          <Typography.Text
            copyable={{ text: v }}
            style={{ fontFamily: 'monospace', fontSize: 11 }}
          >
            {v}
          </Typography.Text>
        ) : (
          '—'
        ),
    },
    {
      title: 'Stage',
      dataIndex: 'current_stage',
      render: (v: number) => (
        <Avatar
          size="small"
          style={{
            background: '#e8f0fe',
            color: '#1d40af',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {v}
        </Avatar>
      ),
    },
    {
      title: 'Outcome',
      dataIndex: 'outcome',
      render: (v: string | null, record: ConversationListItem) => {
        const key = v ?? 'in-progress'
        return (
          <Flex align="center" gap={4}>
            <Tag color={OUTCOME_TAG_COLORS[key] ?? 'default'}>{key}</Tag>
            {record.channel && record.channel !== 'web' && (
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {record.channel}
              </Typography.Text>
            )}
          </Flex>
        )
      },
    },
    {
      title: 'Duration',
      dataIndex: 'duration_secs',
      render: (v: number | null) => fmtDuration(v),
    },
    {
      title: 'Device',
      dataIndex: 'device',
      align: 'center',
      render: (v: string | null) => fmtDevice(v),
    },
    {
      title: 'Loan Request?',
      dataIndex: 'has_loan_questions',
      align: 'center',
      render: (v: boolean) => (v ? 'Yes' : 'No'),
    },
  ]

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Conversations
        </Title>
        <Button icon={<ReloadOutlined />} onClick={() => load(page)}>
          Refresh
        </Button>
      </Flex>

      <Flex wrap gap={8} style={{ marginBottom: 12 }}>
        <Input.Search
          placeholder="Search company ID, phone, prospect…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ flex: '1 1 200px', maxWidth: 320 }}
        />
        <Select
          value={outcome}
          onChange={(v) => setOutcome(v)}
          options={OUTCOME_OPTIONS}
          style={{ width: 150 }}
        />
        <Select
          value={stage}
          onChange={(v) => setStage(v)}
          options={STAGE_OPTIONS}
          style={{ width: 120 }}
        />
        <Select
          value={promptName}
          onChange={handlePromptNameChange}
          options={promptNameOptions}
          style={{ width: 150 }}
        />
        <Select
          value={channel}
          onChange={handleChannelChange}
          options={CHANNEL_OPTIONS}
          style={{ width: 130 }}
        />
        <Select
          value={filterVersionNum}
          onChange={(v) => setFilterVersionNum(v)}
          options={versionDropdownOptions}
          disabled={!versionDropdownEnabled}
          style={{ width: 120 }}
        />
        <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>
          Download CSV
        </Button>
      </Flex>

      <Spin spinning={loading}>
        <Table<ConversationListItem>
          dataSource={conversations}
          columns={columns}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => setSelectedId(record.id),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: page,
            total,
            pageSize: 25,
            showTotal: (t) => `${t} total`,
            onChange: (p) => {
              setPage(p)
              load(p)
            },
          }}
          size="small"
        />
      </Spin>

      <ConversationModal
        conversationId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  )
}
